import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../App"
import { GetShipContext } from "../App/Context"
import { GetShipUsers } from "../Views/ShipView"
import { useEditState } from "../App/useEditState"
import { useSubscription } from "../App/useSubscription"
import { Block, BlockField, BlockRow } from "./Block"
import { CloseButton } from "./CloseButton"
import { Overlay } from "./Overlay"
import { Select, SelectOption } from "./Select"
import { ApiClient, UserApi, IShipUserCrewRole, IShipUser, IShipUserRequest } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { Icons } from "./Icon"
import { logger } from "../App/Logger"

export const ShipUserSelectData = {
  createShipUser(api: ApiClient, shipId: number, values: IShipUserRequest): Promise<{ user: IShipUser }> {
    return new UserApi(api).shipUsers.create({ params: { shipId }, body: values }).call();
  },
  initialSelectedUser(defaultValue?: number | number[]): number | undefined {
    return defaultValue && typeof defaultValue === "number" ? defaultValue : undefined;
  },
  initialSelectedUsers(defaultValue?: number | number[]): number[] | undefined {
    return defaultValue && Array.isArray(defaultValue) ? defaultValue : undefined;
  },
  addSelectedUser(current: number[] | undefined, value: number): number[] {
    const users = current || [];
    return users.indexOf(value) < 0 ? [...users, value] : users;
  },
  removeSelectedUser(current: number[] | undefined, value: number): number[] {
    return (current || []).filter((id) => id != value);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  buildUserValues(values: any, role?: IShipUserCrewRole): any {
    return {
      ...values,
      shipUserCrewRole: role !== undefined ? role : 0,
      shipUserLevel: 0,
    };
  },
  // Display name for a ship user. Invited/registered users can arrive without a
  // joined `name` (the name lives in the users table); fall back to the shortname
  // rather than rendering "Unknown" (#799 Bug 5).
  userLabel(user: { name?: string; shortname?: string }): string {
    return user.name || user.shortname || "Unknown";
  },
  // Dropdown title: "Name (SHORT)" when both are present, otherwise the best
  // available label.
  userTitle(user: { name?: string; shortname?: string }): string {
    return user.name && user.shortname ? `${user.name} (${user.shortname})` : ShipUserSelectData.userLabel(user);
  },
  // A restriction only restricts when it actually names someone: a day whose crew has
  // not been filled in yet passes an empty list, which must mean "everyone is
  // selectable", not "nobody is" (#1213).
  restriction(restrictIds?: number[]): number[] | undefined {
    return restrictIds && restrictIds.length > 0 ? restrictIds : undefined;
  },
  // The dropdown's option list. Kept out of the component so the filtering and
  // ordering rules are directly testable -- they used to live in a `useMemo` that read
  // `restrictIds` without depending on it, leaving a stale list behind whenever the
  // day's crew changed (#1213).
  userOptions({ users, selectedUsers, includeValues, restrictIds, priorityIds, addUserTitle }: {
    users: IShipUser[];
    selectedUsers?: number[];
    includeValues?: number[];
    restrictIds?: number[];
    priorityIds?: number[];
    addUserTitle?: string;
  }): SelectOption[] {
    const restriction = ShipUserSelectData.restriction(restrictIds);
    const isPriority = (value: number) => !!priorityIds && priorityIds.indexOf(value) >= 0;

    const userOptions = users.filter((user) => {
      return user.active > 0
        && (!selectedUsers || selectedUsers.indexOf(user.id) < 0)
        // Values shown as fixed entries (the day's skipper in the crew field) are
        // already part of the selection, so they must not be offered again.
        && (!includeValues || includeValues.indexOf(user.id) < 0)
        && (!restriction || restriction.indexOf(user.id) >= 0);
    }).map((user): SelectOption => {
      return { title: ShipUserSelectData.userTitle(user), value: user.id };
    }).toSorted((a, b) => a.title.localeCompare(b.title));

    return [
      ...(addUserTitle ? [{ title: addUserTitle, value: 0 }] : []),
      ...userOptions.filter((option) => isPriority(option.value as number)),
      ...userOptions.filter((option) => !isPriority(option.value as number)),
    ];
  },
};

export interface IShipUserSelectProps {
  defaultValue?: number | number[];
  dialogOnly?: boolean;
  includeValues?: number[];
  initAddShipUser?: boolean;
  initUserName?: string;
  initUserShortname?: string;
  multi?: boolean;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onChange?: any;
  // Reports the current selection whenever it changes. `onChange` is bound to
  // `useEditState`'s no-arg `onEdited` by every caller, so a form that needs the picked
  // value before it is saved (the day's skipper feeding the crew field) uses this
  // instead (#1213).
  onSelectionChange?: (value?: number | number[]) => void;
  priorityIds?: number[];
  restrictIds?: number[];
  role: IShipUserCrewRole;
  readOnly?: boolean;
}

export function ShipUserSelect({ defaultValue, dialogOnly, includeValues, initAddShipUser, initUserName, initUserShortname, multi, name, onChange, onSelectionChange, priorityIds, restrictIds, role, readOnly }: IShipUserSelectProps) {
  const app = GetApp();
  const t = app.getT("ShipUserSelect");
  const { ship, apiUser } = GetShipContext();
  const shipUsers = GetShipUsers();
  const [addShipUser, setAddShipUser] = useState<boolean>(initAddShipUser ?? false);
  const [addNewUser, setAddNewUser] = useState<number | undefined>(undefined);
  const [n, setN] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<number | undefined>(ShipUserSelectData.initialSelectedUser(defaultValue));
  const [selectedUsers, setSelectedUsers] = useState<number[] | undefined>(ShipUserSelectData.initialSelectedUsers(defaultValue));
  const previousIncludeValues = useRef(includeValues);
  const shipId = ship?.id || app.shipId;
  // Creating a ship user always requires .primary server-side (UserApi.shipUsers.create),
  // regardless of the crew role being assigned — hide the "add new user" affordance
  // entirely for a user who can't perform it (permission matrix, #931, tracker #827).
  const canCreateShipUser = new UserApi(app.apiClient).shipUsers.create({ params: { shipId: shipId || 0 }, body: {} }).available(apiUser);

  const shipUsersForRole = useMemo(() => {
    return (shipUsers || []).filter((user) => user.shipUserCrewRole >= role) as IShipUser[];
  }, [shipUsers, shipUsers?.length, role]);

  const user = useMemo(() => {
    if (!selectedUser) {
      return undefined;
    }
    return shipUsers?.find((user) => user.id == selectedUser);
  }, [selectedUser, shipUsers]);

  const selectUser = useCallback((value?: number) => {
    if (value == undefined) {
      return;
    }
    if (value < 1) {
      setAddShipUser(true);
    } else {
      if (multi) {
        let users = selectedUsers || [];
        if (users.indexOf(value) < 0) {
          users = ShipUserSelectData.addSelectedUser(users, value);
          setSelectedUsers(users);
        }
        if (onSelectionChange) {
          onSelectionChange(users);
        }
      } else {
        setSelectedUser(value);
        if (onSelectionChange) {
          onSelectionChange(value);
        }
      }
      if (onChange) {
        onChange();
      }
    }
  }, [multi, selectedUsers, onChange, onSelectionChange]);

  const removeUser = useCallback((value: number) => {
    const users = ShipUserSelectData.removeSelectedUser(selectedUsers, value);
    setSelectedUsers(users);
    if (onSelectionChange) {
      onSelectionChange(users);
    }
    if (onChange) {
      onChange();
    }
  }, [selectedUsers, onChange, onSelectionChange]);

  // These props arrive as fresh arrays on every parent render, so effects and
  // memos key off their contents rather than their identity.
  const restrictKey = restrictIds ? restrictIds.join(",") : undefined;
  const priorityKey = priorityIds ? priorityIds.join(",") : undefined;
  const includeKey = includeValues ? includeValues.join(",") : undefined;

  useEffect(() => {
    const removedValues = (previousIncludeValues.current || []).filter((value) => !includeValues?.includes(value));
    if (removedValues.length > 0) {
      setSelectedUsers((current) => removedValues.reduce(ShipUserSelectData.addSelectedUser, current));
    }
    previousIncludeValues.current = includeValues;
  }, [includeKey]);

  const users = useMemo(() => {
    if (!shipUsers) {
      return [{ title: t("loading"), value: -1, disabled: true }];
    }
    return ShipUserSelectData.userOptions({
      users: shipUsersForRole,
      selectedUsers,
      includeValues,
      restrictIds,
      priorityIds,
      // Creating a person from a picker that is restricted to an existing list (the
      // watch plan's crew) makes no sense, so the option stays out of it entirely.
      addUserTitle: restrictIds === undefined && canCreateShipUser ? t("add_user") : undefined,
    });
  }, [shipUsers, shipUsersForRole, selectedUsers, canCreateShipUser, restrictKey, priorityKey, includeKey]);

  useEffect(() => {
    if (addNewUser && shipUsers && shipUsers.find((u) => u.id == addNewUser)) {
      selectUser(addNewUser);
      setAddNewUser(undefined);
    } else if (addNewUser) {
      logger.debug("shipuser", "add failed", addNewUser, shipUsers?.map((u) => u.id));
    }
  }, [addNewUser, shipUsers, selectUser, n]);

  useSubscription((user: IShipUser, _type: string, _info: string) => {
    if (!shipUsers || !Array.isArray(shipUsers) || user.shipId != shipId) {
      return;
    }
    if (user.shipId != shipId) {
      return;
    }
    setN((previous) => previous + 1);
  }, Subscription.ShipUser);

  const [_edited, _onEdited, resetEdited] = useEditState();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const createShipUser = useCallback(async (_data: FormData, values: any) => {
    if (!shipId) {
      logger.error("No shipId available");
      return;
    }

    const userValues = ShipUserSelectData.buildUserValues(values, role);

    try {
      const result = await ShipUserSelectData.createShipUser(app.apiClient, shipId, userValues);

      if (result && result.user) {
        setAddNewUser(result.user.id);
        setAddShipUser(false);
        resetEdited();
      }
    } catch (error) {
      logger.error("Error creating user:", error);
    }
  }, [shipId, role, selectUser, resetEdited, shipUsers]);

  return (
    <>
      {addShipUser && (
        <Overlay className="ship-user-dialog" closeCallback={() => setAddShipUser(false)} title={t("dialog_add_user")}>
          <div className="Overlay-body">
            <Block>
              <BlockRow>
                <BlockField label={t("dialog_add_user_name")} className="block_field_small_14">
                  <input type="text" id="user-name" defaultValue={initUserName || ""} required />
                </BlockField>
                <BlockField label={t("dialog_add_user_shortname")} className="block_field_small_10">
                  <input type="text" id="user-shortname" defaultValue={initUserShortname || ""} required maxLength={10} />
                </BlockField>
              </BlockRow>
              <BlockRow actions={true}>
                <BlockField>
                  <button type="button" className="button" onClick={() => setAddShipUser(false)}>{t("dialog_cancel")}</button>
                </BlockField>
                <BlockField>
                  <button type="button" className="button default" onClick={() => {
                    const nameInput = document.getElementById("user-name") as HTMLInputElement;
                    const shortnameInput = document.getElementById("user-shortname") as HTMLInputElement;
                    if (nameInput && shortnameInput && nameInput.value && shortnameInput.value) {
                      const values = {
                        name: nameInput.value,
                        shortname: shortnameInput.value,
                      };
                      createShipUser(new FormData(), values);
                    }
                  }}
                  >{t("dialog_add_new_user")}</button>
                </BlockField>
              </BlockRow>
            </Block>
          </div>
        </Overlay>
      )}

      {dialogOnly ? null : multi ? (
        <div className={`MultiSelect${readOnly ? " readOnly" : ""}`}>
          {Array.from(new Set([...(includeValues || []), ...(selectedUsers || [])])).map((userId, i) => {
            const user = shipUsers?.find((u) => u.id == userId);
            if (user) {
              return (
                <span className="MultiSelect-value" key={`user-${userId}`}>
                  <input type="hidden" name={`${name}[${i}]`} value={userId} />
                  <span className="MultiSelect-value-text">{user.shortname || user.name}</span>
                  {!readOnly && (!includeValues || includeValues.indexOf(userId) < 0) && <CloseButton icon={Icons.Trash} onClick={() => removeUser(user.id)} />}
                </span>
              );
            }
            return null;
          })}
          {!readOnly && <Select button={(user?.shortname || user?.name) || t("select_additional_user")} emptyText={t("no_users")} name={name} value={selectedUser} onSelect={selectUser} options={users} />}
        </div>
      ) : (
        <Select button={(user?.shortname || user?.name) || (readOnly ? "" : t("select_user"))} emptyText={t("no_users")} name={name} value={selectedUser} onSelect={selectUser} options={users} readOnly={readOnly} />
      )}
    </>
  );
}
